import axios, { type AxiosError, type AxiosInstance } from 'axios';
import { useAuthStore } from '@/auth/auth-store';

// Use || (not ??) so an empty-string VITE_API_URL still falls through
// to the relative /api/v1 default. ?? only catches nullish, and Vite
// stamps the literal "" into the bundle when the Docker build arg
// defaults to an empty string. Belt-and-braces with the Dockerfile
// default — either alone would prevent the bundle ending up with
// baseURL = "" (which makes every API call hit the SPA's
// index.html and return HTML for the JSON parser to choke on).
export const apiBaseUrl = import.meta.env.VITE_API_URL || '/api/v1';

export const apiClient: AxiosInstance = axios.create({
  baseURL: apiBaseUrl,
  withCredentials: true,
  timeout: 15_000,
});

// Attach access token, if present, to every request.
apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token !== null && token !== '') {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 401 → drop the session and bounce the user to /login. Refresh-token rotation
// lands in Sprint 5 alongside proper JWT issuance on the server side.
apiClient.interceptors.response.use(
  (res) => res,
  (err: AxiosError) => {
    // VL-400: a step-up request (fresh password for one action) is not an
    // expired session — the screen asks for the password instead.
    const code = (err.response?.data as { code?: unknown } | undefined)?.code;
    const stepUp =
      code === 'reauth_required' || (err.config?.url ?? '').endsWith('/auth/reauthenticate');
    if (err.response?.status === 401 && !stepUp) {
      useAuthStore.getState().logout();
      if (globalThis.window.location.pathname !== '/login') {
        globalThis.window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  },
);
